import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Divider,
  Drawer,
  Group,
  Loader,
  Select,
  Stack,
  Text
} from '@mantine/core';
import { TbArrowLeft, TbFolder, TbFolderCheck, TbRefresh } from 'react-icons/tb';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import {
  useSharepointDrives,
  useSharepointFolders,
  useSharepointSites,
  useSharepointSyncConfig,
  useUpdateSharepointSyncConfig
} from '../../../../services/integrations.service';

const folderListStyles = {
  border: '1px solid #eceef2',
  borderRadius: 12,
  padding: 14
};

export default function SharepointSyncDrawer({ connection, opened, onClose, onSaved }) {
  const connectionId = connection?.connection_id;
  const queryClient = useQueryClient();
  const updateSyncConfig = useUpdateSharepointSyncConfig();

  const [siteId, setSiteId] = useState('');
  const [siteName, setSiteName] = useState('');
  const [driveId, setDriveId] = useState('');
  const [driveName, setDriveName] = useState('');
  const [permissionFolders, setPermissionFolders] = useState([]);
  const [browseFolderId, setBrowseFolderId] = useState('');
  const [folderTrail, setFolderTrail] = useState([]);

  const sharepointConfigQuery = useSharepointSyncConfig({
    params: { connection_id: connectionId },
    queryOptions: {
      enabled: opened && !!connectionId
    }
  });

  const sitesQuery = useSharepointSites({
    params: { connection_id: connectionId },
    queryOptions: {
      enabled: opened && !!connectionId
    }
  });

  const drivesQuery = useSharepointDrives({
    params: { connection_id: connectionId, site_id: siteId },
    queryOptions: {
      enabled: opened && !!connectionId && !!siteId
    }
  });

  const foldersQuery = useSharepointFolders({
    params: {
      connection_id: connectionId,
      site_id: siteId,
      drive_id: driveId,
      ...(browseFolderId ? { folder_id: browseFolderId } : {})
    },
    queryOptions: {
      enabled: opened && !!connectionId && !!siteId && !!driveId
    }
  });

  const siteOptions = useMemo(() => {
    const liveOptions = (sitesQuery.data?.items || []).map(item => ({
      value: item.id,
      label: item.name
    }));
    const fallbackOptions = [
      ...(sharepointConfigQuery.data?.site_id
        ? [
            {
              value: sharepointConfigQuery.data.site_id,
              label: sharepointConfigQuery.data.site_name || sharepointConfigQuery.data.site_id
            }
          ]
        : []),
      ...((sharepointConfigQuery.data?.permitted_folders || []).map(folder => ({
        value: folder.site_id,
        label: folder.site_name || folder.site_id
      })) || [])
    ];

    const seen = new Set();
    return [...fallbackOptions, ...liveOptions].filter(option => {
      if (!option?.value || seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });
  }, [sharepointConfigQuery.data, sitesQuery.data]);

  const driveOptions = useMemo(() => {
    const liveOptions = (drivesQuery.data?.items || []).map(item => ({
      value: item.id,
      label: item.name
    }));
    const fallbackOptions = [
      ...(sharepointConfigQuery.data?.site_id === siteId && sharepointConfigQuery.data?.drive_id
        ? [
            {
              value: sharepointConfigQuery.data.drive_id,
              label: sharepointConfigQuery.data.drive_name || sharepointConfigQuery.data.drive_id
            }
          ]
        : []),
      ...((sharepointConfigQuery.data?.permitted_folders || [])
        .filter(folder => folder.site_id === siteId)
        .map(folder => ({
          value: folder.drive_id,
          label: folder.drive_name || folder.drive_id
        })) || [])
    ];

    const seen = new Set();
    return [...fallbackOptions, ...liveOptions].filter(option => {
      if (!option?.value || seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });
  }, [driveId, drivesQuery.data, sharepointConfigQuery.data, siteId]);

  useEffect(() => {
    if (!opened || !sharepointConfigQuery.data) return;

    const config = sharepointConfigQuery.data;
    setSiteId(config.site_id || '');
    setSiteName(config.site_name || '');
    setDriveId(config.drive_id || '');
    setDriveName(config.drive_name || '');
    setPermissionFolders(config.permitted_folders || []);

    setBrowseFolderId('');
    setFolderTrail([]);
  }, [opened, sharepointConfigQuery.data]);

  const handleSiteChange = value => {
    const option = siteOptions.find(item => item.value === value);
    setSiteId(value || '');
    setSiteName(option?.label || '');
    setDriveId('');
    setDriveName('');
    setBrowseFolderId('');
    setFolderTrail([]);
  };

  const handleDriveChange = value => {
    const option = driveOptions.find(item => item.value === value);
    setDriveId(value || '');
    setDriveName(option?.label || '');
    setBrowseFolderId('');
    setFolderTrail([]);
  };

  const handleOpenFolder = folder => {
    setBrowseFolderId(folder.id);
    setFolderTrail(prev => [...prev, { id: folder.id, name: folder.name }]);
  };

  const handleSelectFolder = folder => {
    const nextFolder = {
      id: folder.id,
      name: folder.name,
      path: folder.path,
      site_id: siteId,
      site_name: siteName,
      drive_id: driveId,
      drive_name: driveName
    };

    setPermissionFolders(prev => {
      if (
        prev.some(folder => folder.id === nextFolder.id && folder.drive_id === nextFolder.drive_id)
      ) {
        return prev;
      }
      return [...prev, nextFolder];
    });
  };

  const handleRemovePermission = folderToRemove => {
    setPermissionFolders(prev =>
      prev.filter(
        folder => !(folder.id === folderToRemove.id && folder.drive_id === folderToRemove.drive_id)
      )
    );
  };

  const handleBrowseBack = () => {
    setFolderTrail(prev => {
      const next = prev.slice(0, -1);
      setBrowseFolderId(next[next.length - 1]?.id || '');
      return next;
    });
  };

  const handleResetToRoot = () => {
    setBrowseFolderId('');
    setFolderTrail([]);
  };

  const handleSave = async () => {
    const primaryPermissionFolder = permissionFolders[0] || null;

    try {
      const response = await updateSyncConfig.mutateAsync({
        connection_id: connectionId,
        workflow_id: sharepointConfigQuery.data?.workflow_id || null,
        workflow_name: sharepointConfigQuery.data?.workflow_name || null,
        site_id: siteId || null,
        site_name: siteName || null,
        drive_id: driveId || null,
        drive_name: driveName || null,
        folder_id: primaryPermissionFolder?.id || null,
        folder_name: primaryPermissionFolder?.name || null,
        folder_path: primaryPermissionFolder?.path || null,
        permitted_folders: permissionFolders,
        enabled: sharepointConfigQuery.data?.enabled || false,
        frequency: sharepointConfigQuery.data?.frequency || null
      });

      queryClient.invalidateQueries(['INTEGRATION_CONNECTIONS']);
      queryClient.invalidateQueries(['INTEGRATION_SHAREPOINT_SYNC_CONFIG']);
      toast.success('SharePoint permissions saved');
      onSaved?.(response);
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save SharePoint permissions');
    }
  };

  const folderItems = foldersQuery.data?.items || [];
  const currentFolder = foldersQuery.data?.current_folder;
  const currentLocationLabel = currentFolder?.name || 'Root folder';

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title="SharePoint Permissions"
      padding="lg"
    >
      <Stack gap="lg">
        <Text size="sm" c="dimmed">
          Tell Datox which SharePoint folders it is allowed to read from. You can add multiple
          folders and only these folders will be shown in the import flow.
        </Text>

        {sharepointConfigQuery.isLoading ? <Loader size="sm" /> : null}

        <Select
          label="SharePoint site"
          placeholder={sitesQuery.isLoading ? 'Loading sites...' : 'Select site'}
          data={siteOptions}
          value={siteId}
          onChange={handleSiteChange}
          searchable
          nothingFoundMessage="No sites found"
          rightSection={sitesQuery.isFetching ? <Loader size={14} /> : null}
        />

        <Select
          label="Document library"
          placeholder={driveId || drivesQuery.isLoading ? 'Select library' : 'Select site first'}
          data={driveOptions}
          value={driveId}
          onChange={handleDriveChange}
          searchable
          disabled={!siteId}
          nothingFoundMessage="No libraries found"
          rightSection={drivesQuery.isFetching ? <Loader size={14} /> : null}
        />

        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <div>
              <Text fw={600} size="sm">
                Permissions
              </Text>
              <Text size="xs" c="dimmed">
                Pick folders Datox is allowed to browse and import files from in this SharePoint
                connection.
              </Text>
            </div>

            <Group gap="xs">
              <Button variant="default" size="xs" onClick={handleResetToRoot} disabled={!driveId}>
                Root
              </Button>
              <Button
                variant="default"
                size="xs"
                leftSection={<TbRefresh size={14} />}
                onClick={() => foldersQuery.refetch()}
                disabled={!driveId}
              >
                Refresh
              </Button>
            </Group>
          </Group>

          <Card withBorder radius="md" style={folderListStyles}>
            <Stack gap="sm">
              <Group justify="space-between">
                <Text size="sm" fw={500}>
                  Browsing: {currentLocationLabel}
                </Text>
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<TbArrowLeft size={14} />}
                  onClick={handleBrowseBack}
                  disabled={folderTrail.length === 0}
                >
                  Back
                </Button>
              </Group>

              {folderTrail.length > 0 || browseFolderId ? (
                <Group justify="space-between" align="flex-start">
                  <div>
                    <Text size="xs" c="dimmed">
                      Current folder
                    </Text>
                    <Text size="sm" fw={500}>
                      {currentFolder?.name || currentLocationLabel}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {currentFolder?.path || 'Drive root'}
                    </Text>
                  </div>
                  <Badge color="blue" variant="light">
                    Browsing
                  </Badge>
                </Group>
              ) : (
                <Text size="sm" c="dimmed">
                  Select folders below to add them to the allowed list.
                </Text>
              )}

              <Divider />

              {!driveId ? (
                <Text size="sm" c="dimmed">
                  Select a site and a document library first.
                </Text>
              ) : foldersQuery.isLoading || foldersQuery.isFetching ? (
                <Loader size="sm" />
              ) : folderItems.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No subfolders found in this location.
                </Text>
              ) : (
                <Stack gap="xs">
                  {folderItems.map(folder => {
                    const isSelected = permissionFolders.some(
                      item => item.id === folder.id && item.drive_id === driveId
                    );
                    return (
                      <Group
                        key={folder.id}
                        justify="space-between"
                        wrap="nowrap"
                        style={{
                          border: '1px solid #eceef2',
                          borderRadius: 10,
                          padding: '10px 12px'
                        }}
                      >
                        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                          <TbFolder size={18} color="#2563eb" />
                          <div style={{ minWidth: 0 }}>
                            <Text size="sm" fw={500}>
                              {folder.name}
                            </Text>
                            <Text size="xs" c="dimmed" truncate>
                              {folder.path || 'Folder'}
                            </Text>
                          </div>
                        </Group>

                        <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                          {isSelected ? (
                            <Badge color="green" variant="light">
                              Selected
                            </Badge>
                          ) : null}
                          <Button
                            variant="default"
                            size="xs"
                            onClick={() => handleOpenFolder(folder)}
                          >
                            Open
                          </Button>
                          <Button
                            size="xs"
                            variant={isSelected ? 'default' : 'light'}
                            leftSection={<TbFolderCheck size={14} />}
                            onClick={() => handleSelectFolder(folder)}
                            disabled={isSelected}
                          >
                            {isSelected ? 'Added' : 'Select'}
                          </Button>
                        </Group>
                      </Group>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          </Card>
        </Stack>

        <Card withBorder radius="md" style={{ backgroundColor: '#fafafa' }}>
          <Stack gap={6}>
            <Text fw={600} size="sm">
              Allowed folders
            </Text>
            {permissionFolders.length === 0 ? (
              <Text size="sm" c="dimmed">
                No folder permissions selected yet.
              </Text>
            ) : (
              <Stack gap="xs">
                {permissionFolders.map(folder => (
                  <Group
                    key={`${folder.drive_id}-${folder.id}`}
                    justify="space-between"
                    wrap="nowrap"
                  >
                    <div>
                      <Text size="sm" fw={500}>
                        {folder.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {folder.site_name || 'Site'} • {folder.drive_name || 'Library'}
                      </Text>
                    </div>
                    <Button
                      variant="subtle"
                      size="xs"
                      color="red"
                      onClick={() => handleRemovePermission(folder)}
                    >
                      Remove
                    </Button>
                  </Group>
                ))}
              </Stack>
            )}
          </Stack>
        </Card>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={updateSyncConfig.isLoading} onClick={handleSave}>
            Save permissions
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
